/**
 * LiveBroadcastPage.tsx — 중개사 라이브 방송 페이지
 * Route: /listings/:id/broadcast (인증 필요)
 * 후면 카메라로 매물 현장 라이브 스트리밍
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMediaStream } from '../../hooks/useMediaStream';
import { useWebRTC } from '../../hooks/useWebRTC';
import { useLiveTour } from './useLiveTour';

export default function LiveBroadcastPage() {
  const { id: listingId = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const media = useMediaStream();
  const rtc   = useWebRTC();
  const tour  = useLiveTour(listingId);

  const localVideoRef = useRef<HTMLVideoElement>(null);
  const [chatInput, setChatInput]   = useState('');
  const [elapsed, setElapsed]       = useState(0);
  const [started, setStarted]       = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // 카메라 시작
  useEffect(() => {
    media.start().then(stream => {
      if (localVideoRef.current) localVideoRef.current.srcObject = stream;
    }).catch(console.error);
    return () => media.stop();
  }, []);

  // 라이브 시작
  const handleStartLive = useCallback(async () => {
    if (!media.localStream) return;
    await tour.startLive();
    await rtc.createRoom(listingId, media.localStream, 'land-tours-rtc');
    setStarted(true);
  }, [media.localStream, listingId, tour, rtc]);

  // 경과 타이머
  useEffect(() => {
    if (!started) return;
    const id = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(id);
  }, [started]);

  // 채팅 스크롤
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [tour.chatMessages]);

  const handleEndLive = async () => {
    await tour.endLive();
    await rtc.hangup(listingId, 'land-tours-rtc');
    navigate(`/listings/${listingId}`);
  };

  const handleSendChat = () => {
    if (!chatInput.trim()) return;
    tour.sendChat(chatInput.trim(), 'broker');
    setChatInput('');
  };

  const elapsedStr = `${String(Math.floor(elapsed / 60)).padStart(2, '0')}:${String(elapsed % 60).padStart(2, '0')}`;

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a12', color: '#f1f5f9', display: 'flex', flexDirection: 'column' }}>
      {/* 헤더 */}
      <div style={{
        padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10,
        borderBottom: '1px solid #1e2030', background: '#0d1117',
      }}>
        <button onClick={() => navigate(`/listings/${listingId}`)}
          style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '1.2rem' }}>
          ←
        </button>
        <span style={{ fontWeight: 700 }}>매물 라이브 투어</span>
        {started && (
          <>
            <span style={{
              background: '#dc2626', color: '#fff', borderRadius: 20,
              padding: '2px 10px', fontSize: '0.72rem', fontWeight: 700,
              display: 'flex', alignItems: 'center', gap: 4,
            }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff', display: 'inline-block' }} />
              LIVE
            </span>
            <span style={{ color: '#f97316', fontSize: '0.8rem', fontWeight: 700 }}>{elapsedStr}</span>
            <span style={{ color: '#64748b', fontSize: '0.8rem' }}>👁 {tour.viewerCount}명 시청 중</span>
          </>
        )}
      </div>

      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 300px', minHeight: 0 }}>
        {/* 카메라 영역 */}
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{
            position: 'relative', background: '#000',
            borderRadius: 12, overflow: 'hidden', flex: 1, minHeight: 0,
          }}>
            <video
              ref={localVideoRef}
              autoPlay playsInline muted
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
            {!started && (
              <div style={{
                position: 'absolute', inset: 0, display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                background: 'rgba(0,0,0,0.5)',
              }}>
                <button
                  onClick={handleStartLive}
                  style={{
                    padding: '18px 40px', borderRadius: 50, border: 'none',
                    background: '#dc2626', color: '#fff',
                    fontSize: '1.2rem', fontWeight: 900, cursor: 'pointer',
                  }}
                >
                  라이브 시작
                </button>
              </div>
            )}
          </div>

          {/* 컨트롤 */}
          <div style={{ display: 'flex', gap: 10 }}>
            <CtrlBtn
              label={media.audioEnabled ? '🎙 마이크 ON' : '🔇 마이크 OFF'}
              on={media.audioEnabled}
              onClick={media.toggleAudio}
            />
            <CtrlBtn
              label={media.videoEnabled ? '📹 카메라 ON' : '📷 카메라 OFF'}
              on={media.videoEnabled}
              onClick={media.toggleVideo}
            />
            <CtrlBtn label="🔄 카메라 전환" on onClick={media.switchCamera} />
            {started && (
              <button
                onClick={handleEndLive}
                style={{
                  marginLeft: 'auto', padding: '10px 20px', borderRadius: 8, border: 'none',
                  background: '#dc2626', color: '#fff', fontWeight: 700, cursor: 'pointer',
                }}
              >
                라이브 종료
              </button>
            )}
          </div>
        </div>

        {/* 채팅 사이드바 */}
        <div style={{
          borderLeft: '1px solid #1e2030', background: '#0d1117',
          display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ padding: '10px 14px', borderBottom: '1px solid #1e2030', fontSize: '0.75rem', fontWeight: 700, color: '#64748b' }}>
            실시간 채팅
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {tour.chatMessages.map(m => (
              <div key={m.id} style={{
                alignSelf: m.sender === 'broker' ? 'flex-end' : 'flex-start',
                background: m.sender === 'broker' ? '#1c5c3a' : '#1e2030',
                borderRadius: 10, padding: '6px 12px',
                fontSize: '0.8rem', color: '#f1f5f9', maxWidth: '80%',
              }}>
                {m.sender === 'viewer' && (
                  <div style={{ fontSize: '0.65rem', color: '#64748b', marginBottom: 2 }}>시청자</div>
                )}
                {m.text}
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>

          <div style={{ padding: 10, borderTop: '1px solid #1e2030', display: 'flex', gap: 6 }}>
            <input
              value={chatInput}
              onChange={e => setChatInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSendChat()}
              placeholder="채팅 입력..."
              style={{
                flex: 1, padding: '8px 10px', borderRadius: 6,
                border: '1px solid #374151', background: '#111827',
                color: '#f1f5f9', fontSize: '0.8rem',
              }}
            />
            <button
              onClick={handleSendChat}
              style={{
                padding: '8px 12px', borderRadius: 6, border: 'none',
                background: '#22c55e', color: '#fff', fontWeight: 700, cursor: 'pointer',
              }}
            >
              전송
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CtrlBtn({ label, on, onClick }: { label: string; on: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '8px 14px', borderRadius: 8,
        border: '1px solid #374151',
        background: on ? '#1e3a2a' : '#1c1c1c',
        color: on ? '#22c55e' : '#64748b',
        fontSize: '0.78rem', cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}
